import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import OTPModal from '../../components/auth/OTPModal'
import SuccessModal from '../../components/auth/SuccessModal'
import Header from '../../components/common/Header'

// Registration flow states
const REGISTRATION_STATES = {
  FORM_FILLING: 'form_filling',
  SUBMITTING: 'submitting',
  OTP_PENDING: 'otp_pending',
  OTP_VERIFYING: 'otp_verifying',
  OTP_ERROR: 'otp_error',
  OTP_EXPIRED: 'otp_expired',
  OTP_RESENDING: 'otp_resending',
  VERIFICATION_SUCCESS: 'verification_success',
  COMPLETING_REGISTRATION: 'completing_registration',
  ERROR: 'error'
}

const AppleRegister = () => {
  const { userType } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  // Get Apple data from location state
  const appleData = location.state?.appleData

  // Main registration state
  const [registrationState, setRegistrationState] = useState(REGISTRATION_STATES.FORM_FILLING)

  // Form data - pre-filled from Apple data
  const [formData, setFormData] = useState({
    firstName: appleData?.firstName || appleData?.given_name || '',
    lastName: appleData?.lastName || appleData?.family_name || '',
    email: appleData?.email || '',
    mobile: '',
    userType: userType?.toUpperCase() || 'PET_GUARDIAN',
    profileImage: null // Apple doesn't provide profile image
  })

  // UI state
  const [activeTab, setActiveTab] = useState('email')
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')

  // OTP state
  const [otpData, setOtpData] = useState({
    verificationId: null,
    target: '',
    sessionData: null,
    expiresAt: null,
    attemptsRemaining: 3,
    canResend: false,
    nextResendTime: null
  })

  // Countdown timer state
  const [timeRemaining, setTimeRemaining] = useState(0)

  // User type configurations
  const userTypeConfig = {
    pet_guardian: {
      title: 'Pet Guardians',
      subtitle: 'For pet parents and guardians',
      primaryColor: '#8548A8',
      mainCircleGradient: 'linear-gradient(135deg, #8548A8 0%, #2A8AD2 100%)',
      floatingCircle1: 'linear-gradient(135deg, #FFA055 0%, #8548A8 100%)',
      floatingCircle2: '#8548A8',
      floatingCircle3: 'linear-gradient(135deg, #FFA055 0%, #FF6B9D 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(255, 160, 85, 0.6)'
    },
    veterinarian: {
      title: 'Veterinary Professional',
      subtitle: 'For licensed veterinarians and veterinary specialists',
      primaryColor: '#00D4B3',
      mainCircleGradient: 'linear-gradient(135deg, #2A8AD2 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #00D4B3 0%, #2A8AD2 100%)',
      floatingCircle2: '#00D4B3',
      floatingCircle3: 'linear-gradient(135deg, #2A8AD2 0%, #00D4B3 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(0, 212, 179, 0.6)'
    },
    service_provider: {
      title: 'Service Provider',
      subtitle: 'For professionals who serve pets and their families',
      primaryColor: '#D65745',
      mainCircleGradient: 'linear-gradient(135deg, #D65745 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #D65745 0%, #00D4B3 100%)',
      floatingCircle2: '#D65745',
      floatingCircle3: 'linear-gradient(135deg, #00D4B3 0%, #D65745 100%)',
      floatingCircle4: '#00D4B3',
      floatingCircle5: 'rgba(214, 87, 69, 0.6)'
    },
    firm: {
      title: 'Firm/Company',
      subtitle: 'For clinics, hospitals, and pet-related businesses',
      primaryColor: '#000080',
      mainCircleGradient: 'linear-gradient(135deg, #000080 0%, #2A8AD2 100%)',
      floatingCircle1: 'linear-gradient(135deg, #000080 0%, #FFA055 100%)',
      floatingCircle2: '#000080',
      floatingCircle3: 'linear-gradient(135deg, #2A8AD2 0%, #000080 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(0, 0, 128, 0.6)'
    }
  }

  const config = userTypeConfig[userType] || userTypeConfig.pet_guardian

  // Check if Apple data exists, redirect if not
  useEffect(() => {
    if (!appleData) {
      console.error('No Apple data found. Redirecting to registration.')
      navigate(`/auth/register/${userType}`)
    }
  }, [appleData, navigate, userType])

  // Timer logic for OTP expiration
  useEffect(() => {
    let interval

    if (timeRemaining > 0 && (registrationState === REGISTRATION_STATES.OTP_PENDING ||
      registrationState === REGISTRATION_STATES.OTP_ERROR)) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setRegistrationState(REGISTRATION_STATES.OTP_EXPIRED)
            setOtpData(prev => ({ ...prev, canResend: true }))
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      clearInterval(interval)
    }

    return () => clearInterval(interval)
  }, [timeRemaining, registrationState])

  // Auto-clear success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {}

    if (!formData.firstName || !formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters'
    }

    if (!formData.lastName || !formData.lastName.trim()) {
      if (userType !== "firm") {
        newErrors.lastName = 'Last name is required'
      }
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters'
    }

    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address'
      }
    }

    // Mobile is optional
    if (activeTab === 'mobile' && formData.mobile && formData.mobile.length < 10) {
      newErrors.mobile = 'Please enter a valid mobile number'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, activeTab])

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target
    let finalValue = value
    if (name === 'mobile') {
      finalValue = value.replace(/[^0-9\+\-\s]/g, '')
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }))

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }, [errors])

  // Handle tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab)
    setErrors({})
  }, [])

  // Error handling
  const handleApiError = useCallback((status, data) => {
    setRegistrationState(REGISTRATION_STATES.ERROR)

    if (status === 400) {
      if (data.detail?.includes('Email already registered')) {
        setErrors({ email: 'This email is already registered. Please try logging in instead.' })
      } else if (data.detail?.includes('Phone already registered')) {
        setErrors({ mobile: 'This phone number is already registered. Please try logging in instead.' })
      } else {
        setErrors({ general: data.detail || 'Registration failed. Please try again.' })
      }
    } else if (status === 422) {
      setErrors({ general: 'Please check your information and try again.' })
    } else if (status === 429) {
      setErrors({ general: 'Too many registration attempts. Please try again later.' })
    } else {
      setErrors({ general: data.detail || 'Registration failed. Please try again.' })
    }
  }, [])

  // Apple registration initiation
  const initiateAppleRegistration = useCallback(async () => {
    if (!validateForm()) return

    setRegistrationState(REGISTRATION_STATES.SUBMITTING)

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      const userTypeMapping = {
        'pet_guardian': 'PET_GUARDIAN',
        'veterinarian': 'VETERINARIAN',
        'service_provider': 'SERVICE_PROVIDER',
        'firm': 'FIRM'
      }

      const backendUserType = userTypeMapping[userType]

      if (!backendUserType) {
        throw new Error('Invalid user type selected')
      }

      // Prepare data for backend
      // We use the apple specific payload structure
      const loginData = {
        apple_id: appleData.sub, // Using sub as apple_id
        email: formData.email.trim(),
        name: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim()
        }
      }

      const registrationData = {
        apple_id: appleData.sub,
        email: formData.email.trim(),
        first_name: formData.firstName.trim(),
        ...(userType !== 'firm' && { last_name: formData.lastName.trim() }),
        user_type: backendUserType,
        device_name: navigator.userAgent
      }


      // First, attempt apple login check
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/apple/verify-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData)
      })

      const data = await response.json()

      if (response.ok) {
        const verifyData = data.data || data
        // User already exists and logged in successfully
        // Store authentication tokens
        if (verifyData.token) {
          localStorage.setItem('access_token', verifyData.token) // Note: Backend might return 'token' or 'access_token' depending on controller
        } else if (verifyData.access_token) {
          localStorage.setItem('access_token', verifyData.access_token)
        }

        if (verifyData.user) {
          localStorage.setItem('user_data', JSON.stringify(verifyData.user))
        }

        // User logged in successfully
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('user-logged-in'));
        }, 1000);

        // Navigate
        if (verifyData.user) {
          navigate('/dashboard', {
            state: {
              user: verifyData.user,
              loginMethod: 'apple',
              isExistingUser: true
            }
          })
        } else {
          // Fallback
          navigate('/dashboard', { state: { loginMethod: 'apple', isExistingUser: true } })
        }

      } else if (response.status === 404 || data.message?.includes('not found') || data.detail?.includes('not found')) {
        // User doesn't exist, proceed with registration flow

        if (activeTab === 'mobile' && formData.mobile) {
          // Send SMS OTP
          const otpResponse = await fetch(`${API_BASE_URL}/api/v1/auth/register/initiate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_type: backendUserType,
              first_name: formData.firstName.trim(),
              last_name: formData.lastName.trim(),
              phone: formData.mobile.trim(),
              language_preference: 'en',
              // We pass social info in a generic way if possible, or handle post-verify
              social_provider: 'apple',
              social_id: appleData.sub
            })
          })

          const otpDataResponse = await otpResponse.json()
          const otpResponseData = otpDataResponse.data || otpDataResponse

          if (otpResponse.ok) {
            // Handle OTP flow
            setOtpData({
              verificationId: otpResponseData.verification_id,
              target: otpResponseData.target,
              // Store registration data to be used after OTP verify
              sessionData: { registrationData },
              expiresAt: new Date(Date.now() + (otpResponseData.expires_in_minutes * 60 * 1000)),
              attemptsRemaining: 3,
              canResend: false,
              nextResendTime: new Date(Date.now() + 60000)
            })

            setTimeRemaining(otpDataResponse.expires_in_minutes * 60)
            setRegistrationState(REGISTRATION_STATES.OTP_PENDING)
            setErrors({})
          } else {
            handleApiError(otpResponse.status, otpDataResponse)
          }
        } else {
          // Email-only registration - directly complete apple registration
          const directRegResponse = await fetch(`${API_BASE_URL}/api/v1/auth/apple/verify-registration`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(registrationData)
          })

          const directRegData = await directRegResponse.json()
          const directRegResponseData = directRegData.data || directRegData

          if (directRegResponse.ok) {
            // Store authentication tokens
            if (directRegResponseData.token) {
              localStorage.setItem('access_token', directRegResponseData.token)
            } else if (directRegResponseData.access_token) {
              localStorage.setItem('access_token', directRegResponseData.access_token)
            }

            if (directRegResponseData.user) {
              localStorage.setItem('user_data', JSON.stringify(directRegResponseData.user))
            }

            // User registered successfully
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('user-logged-in'));
            }, 1000);

            // Navigate to profile completion
            if (directRegResponseData.user) {
              navigate(`/auth/complete-profile/${userType}`, {
                state: {
                  user: directRegResponseData.user,
                  registrationMethod: 'apple',
                  autoLoggedIn: true
                }
              })
            } else {
              // Fallback to login or dashboard
              navigate('/auth/login')
            }
          } else {
            handleApiError(directRegResponse.status, directRegData)
          }
        }
      } else {
        // Handle other errors
        handleApiError(response.status, data)
      }
    } catch (error) {
      console.error('Apple registration error:', error)
      setErrors({ general: 'Network error. Please check your connection and try again.' })
      setRegistrationState(REGISTRATION_STATES.ERROR)
    }
  }, [formData, activeTab, userType, appleData, validateForm, handleApiError, navigate])

  // OTP verification
  const verifyOTP = useCallback(async (code) => {
    setRegistrationState(REGISTRATION_STATES.OTP_VERIFYING)

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      const response = await fetch(`${API_BASE_URL}/api/v1/auth/register/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_id: otpData.verificationId,
          code: code
        })
      })

      const data = await response.json()

      if (response.ok) {
        // OTP verified, now complete Apple registration
        // OTP verification usually returns a token, but for social we might need to finalize
        // However, if we used `register/initiate`, we are in the "phone registration" flow.
        // We need to link this to Apple.

        // Actually, if we use `auth/apple/verify-registration`, it creates the user.
        // If we require OTP, we should probably verify OTP FIRST, and THEN call `auth/apple/verify-registration`
        // But `auth/apple/verify-registration` doesn't take a verification_id.

        // Let's assume for now we just proceed to call verify-registration after OTP success
        // And maybe add the phone number to the payload?

        const finalRegistrationData = {
          ...otpData.sessionData.registrationData,
          phone: formData.mobile.trim(),
          phone_verified: true
          // Backend might not support 'phone' in verify-registration directly if not updated
        }

        const socialRegResponse = await fetch(`${API_BASE_URL}/api/v1/auth/apple/verify-registration`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(finalRegistrationData)
        })

        const socialRegData = await socialRegResponse.json()
        const socialRegResponseData = socialRegData.data || socialRegData

        if (socialRegResponse.ok) {
          if (socialRegResponseData.token) {
            localStorage.setItem('access_token', socialRegResponseData.token)
          } else if (socialRegResponseData.access_token) {
            localStorage.setItem('access_token', socialRegResponseData.access_token)
          }
          if (socialRegResponseData.user) {
            localStorage.setItem('user_data', JSON.stringify(socialRegResponseData.user))
          }

          setRegistrationState(REGISTRATION_STATES.VERIFICATION_SUCCESS)
        } else {
          setErrors({ otp: socialRegData.detail || 'Registration failed after OTP verification.' })
          setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
        }
      } else {
        setOtpData(prev => ({
          ...prev,
          attemptsRemaining: Math.max(0, prev.attemptsRemaining - 1)
        }))

        setErrors({ otp: data.detail || 'Invalid verification code. Please try again.' })
        setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
      }
    } catch (error) {
      console.error('OTP verification error:', error)
      setErrors({ otp: 'Network error. Please check your connection and try again.' })
      setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
    }
  }, [otpData, formData.mobile])

  // Resend OTP
  const resendOTP = useCallback(async () => {
    setRegistrationState(REGISTRATION_STATES.OTP_RESENDING)

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      const response = await fetch(`${API_BASE_URL}/api/v1/auth/register/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_id: otpData.verificationId
        })
      })

      const data = await response.json()

      if (response.ok) {
        const expiresAt = new Date(Date.now() + ((data.expires_in_minutes || 10) * 60 * 1000))

        setOtpData(prev => ({
          ...prev,
          verificationId: data.verification_id || prev.verificationId,
          expiresAt,
          attemptsRemaining: 3,
          canResend: false,
          nextResendTime: new Date(Date.now() + 60000)
        }))

        setTimeRemaining((data.expires_in_minutes || 10) * 60)
        setRegistrationState(REGISTRATION_STATES.OTP_PENDING)
        setSuccessMessage('New verification code sent!')
        setErrors({})
      } else {
        setErrors({ otp: data.detail || 'Failed to resend code.' })
        setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
      }
    } catch (error) {
      console.error('OTP resend error:', error)
      setErrors({ otp: 'Network error. Please try again.' })
      setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
    }
  }, [otpData.verificationId])

  // Event handlers
  const handleSubmit = useCallback(() => {
    initiateAppleRegistration()
  }, [initiateAppleRegistration])

  const handleOTPVerify = useCallback((code) => {
    verifyOTP(code)
  }, [verifyOTP])

  const handleOTPResend = useCallback(() => {
    resendOTP()
  }, [resendOTP])

  const handleSuccessContinue = useCallback(() => {
    setRegistrationState(REGISTRATION_STATES.COMPLETING_REGISTRATION)

    navigate(`/auth/complete-profile/${userType}`, {
      state: {
        user: JSON.parse(localStorage.getItem('user_data') || '{}'),
        registrationMethod: 'apple',
        autoLoggedIn: true
      }
    })
  }, [navigate, userType])

  // Computed values
  const isFormLoading = registrationState === REGISTRATION_STATES.SUBMITTING
  const showOTPModal = [
    REGISTRATION_STATES.OTP_PENDING,
    REGISTRATION_STATES.OTP_VERIFYING,
    REGISTRATION_STATES.OTP_ERROR,
    REGISTRATION_STATES.OTP_EXPIRED,
    REGISTRATION_STATES.OTP_RESENDING
  ].includes(registrationState)

  const showSuccessModal = registrationState === REGISTRATION_STATES.VERIFICATION_SUCCESS

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: '#FAFAFA',
      position: 'fixed',
      top: 0,
      left: 0,
      fontFamily: '"Red Hat Display", sans-serif',
      overflow: 'hidden',
      display: 'flex'
    }}>
      {/* Header Component */}
      <Header />

      {/* Left Section - Design Panel */}
      <div style={{
        width: '50%',
        height: '100vh',
        background: '#FEFEFA',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '140px 60px 60px 60px',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Main Circle with Character */}
        <div style={{
          position: 'relative',
          width: '540px',
          height: '540px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}>
          <img
            src={`/character/${userType === 'pet_guardian' ? 'register-petowner.png' :
              userType === 'veterinarian' ? 'register-vet.png' :
                userType === 'firm' ? 'business.png' :
                'register-service.png'}`}
            alt={`${config.title} character`}
            style={{
              position: 'relative',
              width: '540px',
              height: '540px',
              objectFit: 'cover',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              zIndex: 2
            }}
            onError={(e) => {
              console.error(`Failed to load image: ${e.target.src}`)
              e.target.style.display = 'none'
            }}
          />
        </div>

        {/* Floating Decorative Circles */}
        <div style={{
          position: 'absolute',
          top: '80px',
          left: '40px',
          width: '125px',
          height: '123px',
          background: config.floatingCircle1,
          borderRadius: '50%',
          opacity: 0.6,
          zIndex: 0
        }}></div>

        <div style={{
          position: 'absolute',
          top: '120px',
          right: '80px',
          width: '80px',
          height: '80px',
          background: config.floatingCircle2,
          borderRadius: '50%',
          opacity: 0.8,
          zIndex: 0
        }}></div>

        <div style={{
          position: 'absolute',
          bottom: '100px',
          left: '20px',
          width: '160px',
          height: '160px',
          background: config.floatingCircle3,
          borderRadius: '50%',
          opacity: 0.5,
          zIndex: 0
        }}></div>

        <div style={{
          position: 'absolute',
          bottom: '180px',
          right: '60px',
          width: '100px',
          height: '100px',
          background: config.floatingCircle4,
          borderRadius: '50%',
          opacity: 0.7,
          zIndex: 0
        }}></div>

        <div style={{
          position: 'absolute',
          top: '300px',
          left: '0px',
          width: '60px',
          height: '60px',
          background: config.floatingCircle5,
          borderRadius: '50%',
          opacity: 0.4,
          zIndex: 0
        }}></div>
      </div>

      {/* Right Section - Registration Form */}
      <div style={{
        width: '50%',
        height: '100vh',
        background: '#FFFFFF',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'auto'
      }}>
        {/* Progress Steps */}
        <div style={{
          position: 'absolute',
          top: '140px',
          left: '0',
          right: '0',
          width: '100%',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 80px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '600px'
          }}>
            {/* Step 1 - Active */}
            <div style={{
              width: '40px',
              height: '40px',
              background: config.primaryColor,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#FFFFFF',
              zIndex: 2
            }}>
              1
            </div>

            {/* Connecting Line 1-2 */}
            <div style={{
              flex: 1,
              height: '3px',
              background: '#E5E7EB',
              margin: '0 20px'
            }} />

            {/* Step 2 - Inactive */}
            <div style={{
              width: '40px',
              height: '40px',
              background: '#E5E7EB',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#9CA3AF',
              zIndex: 2
            }}>
              2
            </div>

            {/* Connecting Line 2-3 */}
            <div style={{
              flex: 1,
              height: '3px',
              background: '#E5E7EB',
              margin: '0 20px'
            }} />

            {/* Step 3 - Inactive */}
            <div style={{
              width: '40px',
              height: '40px',
              background: '#E5E7EB',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#9CA3AF',
              zIndex: 2
            }}>
              3
            </div>
          </div>
        </div>

        {/* Form Content Container */}
        <div style={{
          position: 'absolute',
          top: '260px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '600px',
          padding: '0 80px',
          boxSizing: 'border-box'
        }}>
          {/* Form Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '48px'
          }}>
            <h2 style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '38px',
              lineHeight: '1.3',
              color: '#1F2937',
              margin: '0 0 12px 0'
            }}>
              Complete Registration
            </h2>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 400,
              fontSize: '18px',
              lineHeight: '1.5',
              color: '#6B7280',
              margin: 0
            }}>
              Continue with Apple account
            </p>
          </div>

          {/* Apple Account Info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '16px',
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            marginBottom: '24px'
          }}>
            {/* Apple Icon */}
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#000000">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.74 1.18 0 2.21-.82 3.67-.82 1.57.06 2.66.79 3.22 1.62-.27.17-1.78 1.08-1.55 3.52.22 2.39 2.05 3.25 2.11 3.28-.01.03-.5 1.76-1.53 3.63zm-4.32-15.6c.64-.81.97-1.63.85-2.68-.89.04-1.95.6-2.58 1.35-.61.73-.91 1.77-.76 2.65.98.07 1.83-.51 2.49-1.32z" />
            </svg>

            <div style={{ flex: 1 }}>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                color: '#6B7280',
                margin: '0 0 4px 0'
              }}>
                Continuing with Apple account
              </p>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '16px',
                fontWeight: 500,
                color: '#1F2937',
                margin: 0
              }}>
                {formData.email || 'Email hidden'}
              </p>
            </div>
          </div>

          {/* Error Message */}
          {(errors.general || errors.email || errors.mobile) && (
            <div style={{
              padding: '12px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                color: '#EF4444',
                margin: 0
              }}>
                {errors.general || errors.email || errors.mobile}
              </p>
            </div>
          )}

          {/* Name Fields */}
          <div style={{
            display: 'flex',
            gap: '20px',
            marginBottom: '32px'
          }}>
            <div style={{ flex: 1 }}>
              <label style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#374151',
                display: 'block',
                marginBottom: '10px'
              }}>
                First name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter first name"
                disabled={isFormLoading}
                style={{
                  width: '100%',
                  height: '56px',
                  padding: '0 20px',
                  border: `1.5px solid ${errors.firstName ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: isFormLoading ? '#F9FAFB' : '#FFFFFF',
                  cursor: isFormLoading ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.firstName ? '#EF4444' : '#E5E7EB')}
              />
              {errors.firstName && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.firstName}
                </p>
              )}
            </div>

            <div style={{ flex: 1 }}>
              <label style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#374151',
                display: 'block',
                marginBottom: '10px'
              }}>
                Last name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter last name"
                disabled={isFormLoading}
                style={{
                  width: '100%',
                  height: '56px',
                  padding: '0 20px',
                  border: `1.5px solid ${errors.lastName ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: isFormLoading ? '#F9FAFB' : '#FFFFFF',
                  cursor: isFormLoading ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.lastName ? '#EF4444' : '#E5E7EB')}
              />
              {errors.lastName && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.lastName}
                </p>
              )}
            </div>
          </div>

          {/* Email Field - Only show if Apple didn't provide email */}
          {!appleData.email && (
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#374151',
                display: 'block',
                marginBottom: '10px'
              }}>
                Email
              </label>
              <div style={{
                position: 'relative',
                width: '100%'
              }}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2
                  }}
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="22,6 12,13 2,6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  disabled={isFormLoading}
                  style={{
                    width: '100%',
                    height: '56px',
                    padding: '0 20px 0 50px',
                    border: `1.5px solid ${errors.email ? '#EF4444' : '#E5E7EB'}`,
                    borderRadius: '14px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '16px',
                    color: '#1F2937',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: isFormLoading ? '#F9FAFB' : '#FFFFFF',
                    cursor: isFormLoading ? 'not-allowed' : 'text'
                  }}
                  onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                  onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.email ? '#EF4444' : '#E5E7EB')}
                />
              </div>
              {errors.email && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.email}
                </p>
              )}
            </div>
          )}

          {/* Optional Mobile Number */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              color: '#374151',
              display: 'block',
              marginBottom: '10px'
            }}>
              Mobile Number (Optional)
            </label>
            <div style={{
              position: 'relative',
              width: '100%'
            }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2
                }}
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="Mobile (Optional)"
                disabled={isFormLoading}
                style={{
                  width: '100%',
                  height: '56px',
                  padding: '0 20px 0 50px',
                  border: `1.5px solid ${errors.mobile ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: isFormLoading ? '#F9FAFB' : '#FFFFFF',
                  cursor: isFormLoading ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.mobile ? '#EF4444' : '#E5E7EB')}
              />
              {errors.mobile && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.mobile}
                </p>
              )}
            </div>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '12px',
              color: '#6B7280',
              margin: '4px 0 0 0'
            }}>
              {formData.mobile ? 'SMS verification required if provided' : 'Add mobile for SMS notifications'}
            </p>
          </div>

          {/* Submit Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '30px'
          }}>
            <button
              onClick={handleSubmit}
              disabled={isFormLoading}
              style={{
                width: '240px',
                height: '56px',
                background: isFormLoading ? '#E5E7EB' : config.primaryColor,
                borderRadius: '14px',
                border: 'none',
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                color: isFormLoading ? '#9CA3AF' : '#FFFFFF',
                cursor: isFormLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!isFormLoading) {
                  e.target.style.transform = 'translateY(-1px)'
                  e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isFormLoading) {
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'
                }
              }}
            >
              {isFormLoading && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{
                  animation: 'spin 1s linear infinite'
                }}>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {isFormLoading ? 'Creating Account...' : 'Complete Registration'}
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </button>
          </div>

          {/* Back Link */}
          <div style={{
            textAlign: 'center'
          }}>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '14px',
              color: '#9CA3AF',
              margin: 0
            }}>
              Want to use a different account?{' '}
              <span
                onClick={() => !isFormLoading && navigate(`/auth/register/${userType}`)}
                style={{
                  color: config.primaryColor,
                  cursor: isFormLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  opacity: isFormLoading ? 0.5 : 1
                }}
              >
                Back to Registration
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {showOTPModal && (
        <OTPModal
          isOpen={true}
          primaryColor={config.primaryColor}
          target={otpData.target}
          timeRemaining={timeRemaining}
          attemptsRemaining={otpData.attemptsRemaining}
          canResend={otpData.canResend}
          isVerifying={registrationState === REGISTRATION_STATES.OTP_VERIFYING}
          isResending={registrationState === REGISTRATION_STATES.OTP_RESENDING}
          error={errors.otp}
          onVerify={handleOTPVerify}
          onResend={handleOTPResend}
          onClose={() => {/* handle close */ }}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          isOpen={true}
          title={t('register.oauth.apple.title', 'Apple Registration Success')}
          message={t('register.oauth.apple.message', 'Your account has been successfully created with Apple.')}
          buttonText={t('register.success.button')}
          loadingText={t('register.success.loading')}
          target={otpData.target}
          variant="registration"
          onContinue={handleSuccessContinue}
        />
      )}
    </div>
  )
}

export default AppleRegister